import { useEffect, useState } from 'react';
import { Icon } from '../../design-system/icons';
import { descargarArchivo } from '../../utils/descargarArchivo';
import { buildDownloadFileName } from '../../utils/downloadFileName';
import type { ResultImage } from './accountSetup';

/**
 * Vista full screen de una imagen de resultado (Figma node 173:227, ADR-724).
 * Se abre al tocar una de las imágenes generadas. Fondo oscuro con blur, botón de
 * cerrar, la imagen grande, un botón "Descargar" y una tarjeta con lo que se puede
 * hacer una vez dentro de Atlas. Entra con fade + slide-up; cierra con Escape,
 * click-fuera o la X.
 */
export interface ResultImageOverlayProps {
  image: ResultImage;
  onClose: () => void;
  /**
   * Se dispara cuando el usuario toca "Descargar". En mobile ésta es la ÚNICA
   * descarga posible (la grilla de resultados no tiene botón propio), así que es
   * la señal que habilita el upsell de planes al salir del alta (ADR-2026-07-29-upsell-planes-salir-alta-condicionado).
   */
  onDownload?: () => void;
}

export function ResultImageOverlay({ image, onClose, onDownload }: ResultImageOverlayProps) {
  const [shown, setShown] = useState(false);

  // Progresivo: la optimizada (`src`) se muestra ya (viene cacheada de la grilla) y
  // se hace swap a la ORIGINAL full-res (`full`) cuando termina de cargar —que suele
  // ser al instante, porque la pantalla de resultados la precarga por detrás. Si no
  // hay una `full` distinta, se usa `src` directo (sin swap).
  const optimized = image.src;
  const original = image.full ?? image.src;
  const needsUpgrade = original !== optimized;
  const [originalReady, setOriginalReady] = useState(!needsUpgrade);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Carga (o detecta desde caché) la original y hace el swap al terminar. Ante error
  // NO cambia → se queda mostrando la optimizada.
  useEffect(() => {
    if (!needsUpgrade) {
      setOriginalReady(true);
      return;
    }
    setOriginalReady(false);
    const loader = new Image();
    loader.onload = () => setOriginalReady(true);
    loader.src = original;
    // Si ya estaba en caché (precargada), `complete` es true de una.
    if (loader.complete && loader.naturalWidth > 0) setOriginalReady(true);
    return () => {
      loader.onload = null;
    };
  }, [original, needsUpgrade]);

  const displaySrc = originalReady ? original : optimized;

  // Descarga: mismo mecanismo que el resto de la app (`utils/descargarArchivo`).
  // Antes era un `<a href download>` a la URL remota, y en iOS eso NO guarda nada —
  // Safari ignora el atributo `download` y abre la imagen en otra vista. Justo el
  // dispositivo donde se hace el onboarding.
  // Ver ADR-2026-08-03-descarga-del-onboarding-usa-el-mecanismo-de-la-app.
  const esVideo = !!(image.isVideo && image.video);
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState(false);

  const handleDownload = async () => {
    if (descargando) return;
    setDescargando(true);
    setErrorDescarga(false);
    try {
      const ok = await descargarArchivo({
        url: esVideo ? image.video! : original,
        fileName: buildDownloadFileName({ variantName: image.label, forceVideo: esVideo }),
        esVideo,
      });
      // `onDownload` habilita el upsell de planes al salir del alta, así que sólo se
      // dispara si el archivo llegó de verdad. Cancelar la hoja de compartir no cuenta.
      if (ok) onDownload?.();
    } catch {
      setErrorDescarga(true);
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Vista ampliada: ${image.label}`}
      onClick={onClose}
      className={`fixed inset-0 z-50 flex justify-center bg-black/80 p-5 backdrop-blur-[5px] transition-opacity duration-300 ease-out ${
        shown ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`flex h-full w-full max-w-[402px] flex-col gap-6 pb-2 pt-[calc(env(safe-area-inset-top)+30px)] transition-transform duration-300 ease-out ${
          shown ? 'translate-y-0' : 'translate-y-4'
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="-m-1 shrink-0 self-start rounded-full p-1 text-[#FAFAFA] transition-colors hover:bg-white/10"
        >
          <Icon name="outline/cross" size={24} />
        </button>

        <div className="flex min-h-0 flex-1 items-center justify-center">
          {image.isVideo && image.video ? (
            <video
              src={image.video}
              poster={image.src}
              autoPlay
              muted
              loop
              playsInline
              controls
              className="max-h-full w-full rounded-[16px] object-contain"
            />
          ) : (
            // `max-w-full max-h-full` sin `w-full`: la imagen se escala hasta entrar en
            // el hueco disponible y el navegador le mantiene la **relación de aspecto**
            // (alto y ancho automáticos, acotados por los `max-*`). Antes era
            // `w-full object-cover`, que la estiraba al ancho del contenedor y
            // **recortaba** lo que sobraba de alto: en la vista ampliada se veía un
            // encuadre distinto del de la imagen generada
            // (ADR-2026-08-04-vista-ampliada-respeta-aspect-ratio). De paso, el
            // `rounded` ahora abraza la imagen y no una caja con franjas vacías.
            <img
              src={displaySrc}
              alt={image.label}
              draggable={false}
              className="max-h-full max-w-full rounded-[16px] object-contain"
            />
          )}
        </div>

        <div className="flex w-full shrink-0 flex-col gap-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={descargando}
            className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-white p-4 text-[16px] font-[700] leading-[19px] text-[#18181B] transition-colors hover:bg-[#F4F4F5] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {descargando ? 'Descargando…' : 'Descargar'}
          </button>
          {/* Un fallo de descarga no puede pasar en silencio: el usuario tocó el botón
              y se quedaría mirando una pantalla que no hizo nada. */}
          {errorDescarga && (
            <p role="alert" className="text-center text-[13px] font-[500] leading-[18px] text-[#FCA5A5]">
              No pudimos descargar el archivo. Probá de nuevo.
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-start gap-2.5 rounded-[16px] border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
          <Icon name="solid/sparkles" size={20} className="mt-0.5 shrink-0 text-[#FAFAFA]" />
          <div className="flex flex-col gap-1.5">
            <p className="text-[14px] font-[600] leading-[19px] text-[#FAFAFA]">
              Una vez ingresado a Estudio Atlas vas a poder disfrutar de:
            </p>
            <p className="text-[13px] font-[400] leading-[18px] text-[#FAFAFA]/80">
              Edición de imagen, Animación, Creación de anuncios, Carrusel, Mejorar calidad, entre otros.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResultImageOverlay;
